import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ColumnDef,
  ColumnFiltersState,
  ExpandedState,
  FilterFn,
  PaginationState,
  Row as TableRow,
  SortingState,
  createColumnHelper,
  filterFns,
} from "@tanstack/react-table";
import { FormEvent, useCallback, useContext, useMemo, useState } from "react";
import { Alert, Col, Form, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import type { FirmsController } from "../../../api/src/firms/firms.controller";
import type { ProfileController } from "../../../api/src/profile/profile.controller";
import type { RebalancesController } from "../../../api/src/rebalances/rebalances.controller";
import Loading from "../Loading";
import FormError from "../components/FormError";
import { NotificationContext } from "../Notifications";
import ActionButton from "../components/ActionButton";
import { RowExpander } from "../components/Table/RowExpander";
import { Table, metaDefault, useTable } from "../components/Table/Table";
import {
  AssignedToFilter,
  multiSelectIncludes,
} from "../components/Table/filters";
import {
  useSlantConnected,
  makeServiceTierColumn,
  serviceTierFilterFn,
} from "../components/Table/serviceTier";
import {
  onColumnFiltersChange,
  onPaginationChange,
  onSortingChange,
  useTableSettings,
} from "../components/Table/tableSettings";
import {
  processEmptyResponse,
  refusalSentence,
  serverError,
  useAuthenticatedFetch,
  useAuthenticatedMutation,
  useAuthenticatedMutationAsync,
} from "../lib/api";
import { useAuthenticatedPercentFetch } from "../clients/percents";
import { processFileResponse } from "../lib/file";
import { formatCurrency } from "../lib/numbers";
import RebalanceQueryControls from "./RebalanceQueryControls";
import { deserializeRebalanceListPercents } from "./percents";
import {
  getRebalanceRowId,
  getRebalanceSubRows,
  useRebalanceListRows,
  withoutServiceTierFilter,
} from "./useRebalanceListRows";
import { validateRqlQuery } from "../lib/rql";
import {
  RebalanceReasonFilter,
  RebalanceRow,
  getRealizedGainLoss,
  getRiskAllocationDisplay,
  getSelectedRebalanceRows,
  groupPositionSwapRows,
  positionSwapRowId,
  rebalanceTypeDisplay,
  rebalanceTypeFilterOptions,
} from "./lib";

const columnHelper = createColumnHelper<RebalanceRow>();

/**
 * `filterFn` for the group row of a swap: the group row always passes, since
 * the tab has already left out each proposal of the swap that fails a filter
 * (`swapProposalsThatFail`) and keeps a group only with a proposal left
 * (APP-1753, R24, R25). Every other row passes as `filterFn` says.
 */
function passOnASwapGroup(
  filterFn: FilterFn<RebalanceRow>,
): FilterFn<RebalanceRow> {
  const passOnGroup: FilterFn<RebalanceRow> = (
    row,
    columnId,
    filterValue,
    addMeta,
  ) =>
    row.original.level === "swap" ||
    filterFn(row, columnId, filterValue, addMeta);
  passOnGroup.autoRemove = filterFn.autoRemove;
  passOnGroup.resolveFilterValue = filterFn.resolveFilterValue;
  return passOnGroup;
}

/**
 * The ids of the proposals of a swap in `rows` that fail one or more of
 * `columnFilters`, each tested as its column of `columns` tests a row of the
 * table, joined into one key (APP-1753, R25, R41).
 */
function swapProposalsThatFail(
  rows: RebalanceRow[],
  columns: ColumnDef<RebalanceRow, unknown>[],
  columnFilters: ColumnFiltersState,
): string {
  const tests = columnFilters.flatMap((filter) => {
    const column = columns.find(
      (def) =>
        def.id === filter.id ||
        ("accessorKey" in def && def.accessorKey === filter.id),
    );
    if (column === undefined || typeof column.filterFn !== "function") {
      return [];
    }
    const filterFn = column.filterFn;
    const value = (row: RebalanceRow, index: number) =>
      "accessorFn" in column
        ? column.accessorFn(row, index)
        : "accessorKey" in column
          ? row[column.accessorKey as keyof RebalanceRow]
          : undefined;
    const filterValue =
      filterFn.resolveFilterValue?.(filter.value) ?? filter.value;
    return filterFn.autoRemove?.(filterValue, undefined)
      ? []
      : [
          (row: RebalanceRow, index: number) =>
            filterFn(
              {
                original: row,
                getValue: () => value(row, index),
              } as unknown as TableRow<RebalanceRow>,
              filter.id,
              filterValue,
              () => {},
            ),
        ];
  });
  return rows
    .filter(
      (row, index) =>
        row.positionSwapId != null && !tests.every((test) => test(row, index)),
    )
    .map((row) => row.rebalanceId)
    .join(",");
}

/**
 * APP-1228: `publicFetch` (lib/api.ts) throws `Error("Request failed: <body>")`
 * on ANY non-ok response, so the block-submit mutation rejects on the 502
 * orphaned-execution path — the structured body never lands in `result`. That
 * body still carries the operator-facing sentence and the lot-truncation
 * warnings we must surface (especially the "order is live at Schwab without
 * allocations" case). Parse them back out of the thrown error.
 *
 * APP-1526: reads the api's documented error body
 * (`api/src/lib/errorBody.ts`). Two things moved under it. The sentence is
 * `message`, not `error` — the endpoint used to be the only one in the api
 * calling it `error`, which is why this parser had to exist separately from
 * `extractServerMessage` (`lib/api.ts`) at all. And the
 * warnings are in `details`, where every route-specific fact now lives.
 * Recovery from the thrown error is `serverError`'s, shared with the other
 * three console parsers.
 */
export function parseSubmitError(err: unknown): {
  message: string | null;
  warnings: string[];
} {
  const body = serverError(err)?.body;
  const warnings = body?.details?.warnings;
  return {
    message:
      typeof body?.message === "string" && body.message.length > 0
        ? body.message
        : null,
    warnings: Array.isArray(warnings)
      ? warnings.filter((w): w is string => typeof w === "string")
      : [],
  };
}

const RebalancesApprovedTable = ({ householdId }: { householdId?: string }) => {
  const [page] = useState(1);
  const [pageSize] = useState(1000); // APP-1308: show all approved rebalances on one page (client-side pager handles display); server pager is the proper follow-up
  const [rqlQuery, setRqlQuery] = useState("");

  // Handle RQL query changes
  const handleRqlQueryChange = (query: string) => {
    setRqlQuery(query);
  };

  // Schwab API submission state
  const [submitResult, setSubmitResult] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  // APP-1228: lot-truncation warnings live in their OWN state slot so that
  // (a) dismissing the success Alert doesn't unmount the warnings Alert
  // (operator might dismiss the green banner before reading the yellow one),
  // and (b) the warnings Alert's onClose can use a plain setter without
  // racing the submitResult state via a stale-closure spread.
  const [truncationWarnings, setTruncationWarnings] = useState<string[]>([]);

  // Fetch user profile to check Schwab connection status
  const { data: dataProfile } =
    useAuthenticatedFetch<UnpackResponse<ProfileController["getProfile"]>>(
      "/profile",
    );
  const hasSchwabConnected = dataProfile?.data?.connections?.schwab ?? false;

  // Fetch firm data to check for Schwab advisor codes
  const { data: dataFirm } =
    useAuthenticatedFetch<
      SerializedObject<UnpackResponse<FirmsController["getCurrentFirm"]>>
    >("/firm/current");
  const hasSchwabMasterAccount =
    dataFirm?.data?.advisorCodes?.some((ac) => ac.custodian === "SWB") ?? false;

  const canSubmitToSchwab = hasSchwabConnected && hasSchwabMasterAccount;

  // APP-1279/APP-1280: filtering is pushed down to SQL. Send the RQL as &q= so
  // the server filters firm-wide; only append a syntactically valid query.
  const rqlValidation = validateRqlQuery(rqlQuery);
  // APP-1652: `GET /rebalances/filter` is served at `GET /v1/rebalances` under
  // `limit`/`offset`. One page of `pageSize` rows, as before (APP-1308).
  const queryKey = `/v1/rebalances?status=approved&metrics=true&limit=${pageSize}&offset=${(page - 1) * pageSize}${
    householdId ? `&householdId=${householdId}` : ""
  }${
    rqlQuery.trim() && rqlValidation.success
      ? `&q=${encodeURIComponent(rqlQuery)}`
      : ""
  }`;

  const {
    isPending: isPendingRebalances,
    isError: isErrorRebalances,
    data: dataRebalances,
  } = useAuthenticatedPercentFetch<
    UnpackResponse<RebalancesController["getAllApprovedRebalances"]>
  >(queryKey, deserializeRebalanceListPercents);
  const rebalances = dataRebalances?.data;

  const slantConnected = useSlantConnected();

  const [tableSettings, setTableSettings] =
    useTableSettings("rebalances-today");
  const {
    rebalanceRows: listRows,
    isPendingAssignees,
    filterBarItems,
    setRowSelectionRef,
  } = useRebalanceListRows(rebalances, tableSettings, setTableSettings);

  const queryClient = useQueryClient();

  // APP-1486, swap spec R120: the API refuses an action on a position swap's
  // proposal with 409 and a sentence naming the swap. A list a later write
  // made out of date can still send such a proposal, so the refusal is shown.
  const { pushNotification } = useContext(NotificationContext);
  const notifyError = useCallback(
    (header: string) => (error: unknown) =>
      pushNotification({
        id: "rebalances-approved-error",
        header,
        body: refusalSentence(error) ?? "Something went wrong; try again.",
        variant: "danger",
      }),
    [pushNotification],
  );

  const executeRebalances = useAuthenticatedMutationAsync<
    UnpackResponse<RebalancesController["changeRebalancesState"]>
  >(
    "/rebalances",
    async () => {
      const rebalanceIds = getSelectedRebalanceRows(rowSelection);

      return {
        method: "PATCH",
        body: JSON.stringify({
          state: "executing",
          trade_format: "allocation",
          rebalanceIds,
        }),
      };
    },
    processFileResponse,
  );

  const executeBlockTrading = useAuthenticatedMutationAsync<
    UnpackResponse<RebalancesController["changeRebalancesState"]>
  >(
    "/rebalances",
    async () => {
      const rebalanceIds = getSelectedRebalanceRows(rowSelection);

      return {
        method: "PATCH",
        body: JSON.stringify({
          state: "executing",
          trade_format: "block",
          rebalanceIds,
        }),
      };
    },
    processFileResponse,
  );

  // APP-1526: the endpoint answers a successful block submit with
  // `wrapResponse({ warnings })` — `{ data: { warnings } }` — like every other
  // success body in the api. `success` and `error` are gone from it: a failure
  // now leaves as an exception carrying the documented error body, so this
  // promise RESOLVING is what success means.
  const submitBlockTrading = useAuthenticatedMutationAsync<{
    // APP-1228: operator-facing notices from the Schwab submission
    // (always present, empty array when none).
    data: { warnings: string[] };
  }>("/rebalances", async (rebalanceIds: number[]) => ({
    method: "PATCH",
    body: JSON.stringify({
      state: "executing",
      trade_format: "block",
      method: "api",
      rebalanceIds,
    }),
  }));

  const mutation = useMutation({
    mutationFn: async (event: FormEvent) => {
      event.preventDefault();

      // Get these before the async request for data consistency
      const selectedRebalanceIds = getSelectedRebalanceRows(rowSelection);
      const unselectedRebalances = (rebalances ?? [])?.filter(
        (rebalance) => !selectedRebalanceIds.includes(rebalance.id),
      );

      await executeRebalances();

      // Manually reset the row selection; otherwise the previous row selection
      // remains in the table state even after modifying its data.
      setRowSelection({});
      queryClient.setQueryData([queryKey], {
        data: unselectedRebalances,
      });
    },
    onError: notifyError("Execute failed"),
  });

  const blockTradingMutation = useMutation({
    mutationFn: async (event: FormEvent) => {
      event.preventDefault();

      // Get these before the async request for data consistency
      const selectedRebalanceIds = getSelectedRebalanceRows(rowSelection);
      const unselectedRebalances = (rebalances ?? [])?.filter(
        (rebalance) => !selectedRebalanceIds.includes(rebalance.id),
      );

      await executeBlockTrading();

      // Manually reset the row selection; otherwise the previous row selection
      // remains in the table state even after modifying its data.
      setRowSelection({});
      queryClient.setQueryData([queryKey], {
        data: unselectedRebalances,
      });
    },
    onError: notifyError("Block trading failed"),
  });

  const submitBlockTradingMutation = useMutation({
    mutationFn: async (event: FormEvent) => {
      event.preventDefault();
      setSubmitResult(null);
      setTruncationWarnings([]);

      const selectedRebalanceIds = getSelectedRebalanceRows(rowSelection);
      if (selectedRebalanceIds.length === 0) {
        setSubmitResult({
          type: "error",
          message: "Please select at least one rebalance to submit.",
        });
        return;
      }

      const unselectedRebalances = (rebalances ?? [])?.filter(
        (rebalance) => !selectedRebalanceIds.includes(rebalance.id),
      );

      let result: { data: { warnings: string[] } };
      try {
        result = await submitBlockTrading(selectedRebalanceIds);
      } catch (err) {
        // APP-1228: a 502 (incl. the orphaned-execution case) rejects here —
        // recover the warnings and the specific server message from the thrown
        // error body so the operator still sees both.
        const parsed = parseSubmitError(err);
        setTruncationWarnings(parsed.warnings);
        setSubmitResult({
          type: "error",
          message: parsed.message ?? "Failed to submit trades to Schwab.",
        });
        return;
      }

      // APP-1526: no `result.success` branch any more. Every refusal the
      // endpoint can answer with — Schwab not connected, no Schwab trades, no
      // file generated, either upload refused — is a non-2xx and therefore an
      // exception, handled above. Reaching this line means Schwab accepted both
      // uploads.
      setTruncationWarnings(result?.data?.warnings ?? []);
      setSubmitResult({
        type: "success",
        message: "Trades successfully submitted to Schwab.",
      });
      setRowSelection({});
      queryClient.setQueryData([queryKey], {
        data: unselectedRebalances,
      });
    },
  });

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "select",
        header: (info) => (
          <Form.Check
            checked={info.table.getIsAllRowsSelected()}
            onChange={info.table.getToggleAllPageRowsSelectedHandler()}
          />
        ),
        aggregatedCell: (info) => (
          <Form.Check
            id={
              info.row.original.level === "swap"
                ? `select-${info.row.id}`
                : `select-${info.row.original.rebalanceId}`
            }
            checked={info.row.getIsSelected()}
            onChange={info.row.getToggleSelectedHandler()}
          />
        ),
      }),
      columnHelper.accessor("name", {
        cell: (info) => (
          <div
            style={{
              // Since rows are flattened by default,
              // we can use the row.depth property
              // and paddingLeft to visually indicate the depth
              // of the row
              paddingLeft: `${info.row.depth * 2}rem`,
            }}
          >
            {info.getValue()}
          </div>
        ),
        aggregatedCell: (info) =>
          info.row.original.level === "swap" ? (
            // The group of a swap stays open; its header links to the swap.
            <Link
              to={`/rebalances/position-swaps/${info.row.original.positionSwapId}`}
              className="fw-bold"
            >
              {info.getValue()}
            </Link>
          ) : (
            <div style={{ paddingLeft: `${info.row.depth * 2}rem` }}>
              {info.row.getCanExpand() ? <RowExpander row={info.row} /> : ""}{" "}
              <Link to={`/rebalances/${info.row.original.rebalanceId}`}>
                {info.getValue()}
              </Link>
            </div>
          ),
        header: () => "Household",
        minSize: 225,
        // The group row of a swap is named for the swap, so it passes; the
        // filter has already left out its households that fail.
        filterFn: passOnASwapGroup(filterFns.includesString),
      }),
      // Service Tier (Slant) — immediately after Household, a column and a
      // filter (R23). Only when the user is Slant-connected
      // (`connections.slant` on GET /profile); otherwise the column + filter
      // are omitted entirely (F4). The group row of a swap has no tier: it
      // passes, and the filter has already left out its households that fail.
      ...(slantConnected
        ? [
            {
              ...makeServiceTierColumn(columnHelper),
              filterFn: passOnASwapGroup(
                serviceTierFilterFn as FilterFn<RebalanceRow>,
              ),
            },
          ]
        : []),
      columnHelper.accessor(
        (row: RebalanceRow) => {
          if (row.level === "account" || typeof row.type === "undefined") {
            return "";
          } else if (
            row.type &&
            Object.prototype.hasOwnProperty.call(rebalanceTypeDisplay, row.type)
          ) {
            const option = rebalanceTypeFilterOptions.find(
              (a) =>
                a.label ===
                rebalanceTypeDisplay[
                  row.type as keyof typeof rebalanceTypeDisplay
                ],
            );
            return option?.value.toString() ?? "";
          }
          return "";
        },
        {
          id: "type",
          header: () => "Trade Proposal Reason",
          cell: (info) => {
            const option = rebalanceTypeFilterOptions.find(
              (a) => a.value.toString() === info.getValue(),
            );
            return option?.label ?? "";
          },
          aggregatedCell: (info) => {
            const option = rebalanceTypeFilterOptions.find(
              (a) => a.value.toString() === info.getValue(),
            );
            return option?.label ?? "";
          },
          filterFn: passOnASwapGroup(multiSelectIncludes),
          meta: {
            filterComponent: RebalanceReasonFilter,
          },
        },
      ),
      columnHelper.accessor((row) => row.assignedUser?.id.toString() ?? "", {
        id: "assignedUserId",
        cell: (info) => info.row.original.assignedUser?.name ?? "",
        aggregatedCell: (info) => info.row.original.assignedUser?.name ?? "",
        header: () => "Assigned To",
        minSize: 100,
        // A swap's group row has no assignee: it passes, and the filter has
        // already left out its proposals that fail.
        filterFn: passOnASwapGroup(multiSelectIncludes),
        meta: {
          filterComponent: AssignedToFilter,
        },
      }),
      columnHelper.accessor((row) => row.riskAllocation?.current.growth ?? 0, {
        id: "current",
        cell: (info) =>
          getRiskAllocationDisplay(info.row.original.riskAllocation?.current),
        aggregatedCell: (info) =>
          getRiskAllocationDisplay(info.row.original.riskAllocation?.current),
        header: () => "Current Allocation",
        enableColumnFilter: false,
        enableSorting: false,
        meta: { className: "text-nowrap" },
      }),
      columnHelper.accessor(
        (row) => row.riskAllocation?.postRebalance.growth ?? 0,
        {
          id: "postRebalance",
          cell: (info) =>
            getRiskAllocationDisplay(
              info.row.original.riskAllocation?.postRebalance,
            ),
          aggregatedCell: (info) =>
            getRiskAllocationDisplay(
              info.row.original.riskAllocation?.postRebalance,
            ),
          header: () => "Post-Trade Proposal Allocation",
          enableColumnFilter: false,
          enableSorting: false,
          meta: { className: "text-nowrap" },
        },
      ),
      columnHelper.accessor((row) => row.riskAllocation?.target.growth ?? 0, {
        id: "target",
        cell: (info) =>
          getRiskAllocationDisplay(info.row.original.riskAllocation?.target),
        aggregatedCell: (info) =>
          getRiskAllocationDisplay(info.row.original.riskAllocation?.target),
        header: () => "Target Allocation",
        enableColumnFilter: false,
        enableSorting: false,
        meta: { className: "text-nowrap" },
      }),
      columnHelper.accessor((row) => row.totalBalance ?? 0, {
        id: "totalBalance",
        cell: (info) => formatCurrency(info.row.original.totalBalance),
        aggregatedCell: (info) =>
          formatCurrency(info.row.original.totalBalance),
        header: () => "AUM",
        enableColumnFilter: false,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
      }),
      columnHelper.accessor((row) => row.cashBalance ?? 0, {
        id: "cashBalance",
        cell: (info) => formatCurrency(info.row.original.cashBalance),
        aggregatedCell: (info) => formatCurrency(info.row.original.cashBalance),
        header: () => "Cash",
        enableColumnFilter: false,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
      }),
      columnHelper.accessor((row) => row.buyTotal ?? 0, {
        id: "buyTotal",
        cell: (info) => formatCurrency(info.getValue()),
        aggregatedCell: (info) => formatCurrency(info.getValue()),
        header: () => "Buy",
        enableColumnFilter: false,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
      }),
      columnHelper.accessor((row) => row.sellTotal ?? 0, {
        id: "sellTotal",
        cell: (info) => formatCurrency(info.getValue()),
        aggregatedCell: (info) => formatCurrency(info.getValue()),
        header: () => "Sell",
        enableColumnFilter: false,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
      }),
      columnHelper.accessor(
        (row) => (row.sellTotal ?? 0) - (row.buyTotal ?? 0),
        {
          id: "net",
          cell: (info) => formatCurrency(info.getValue()),
          aggregatedCell: (info) => formatCurrency(info.getValue()),
          header: () => "Net",
          enableColumnFilter: false,
          meta: {
            headerClassName: "text-end",
            className: "text-end",
          },
        },
      ),
      columnHelper.accessor((row) => getRealizedGainLoss(row) ?? 0, {
        id: "realizedGainLoss",
        cell: (info) => formatCurrency(getRealizedGainLoss(info.row.original)),
        aggregatedCell: (info) =>
          formatCurrency(getRealizedGainLoss(info.row.original)),
        header: () => "Realized Gain/Loss",
        enableColumnFilter: false,
        meta: {
          headerClassName: "text-end",
          className: "text-end",
        },
      }),
      columnHelper.display({
        id: "actions",
        meta: {
          className: "text-nowrap",
        },
        aggregatedCell: (info) => {
          const rebalance = info.row.original;

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const rejectRebalance = useAuthenticatedMutation<
            UnpackResponse<RebalancesController["changeRebalancesState"]>
          >(
            "/rebalances",
            {
              method: "PATCH",
              body: JSON.stringify({
                state: "proposed",
                rebalanceIds: [rebalance.rebalanceId],
              }),
            },
            processEmptyResponse,
          );

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const cancelRebalance = useAuthenticatedMutation<
            UnpackResponse<RebalancesController["changeRebalancesState"]>
          >(
            "/rebalances",
            {
              method: "PATCH",
              body: JSON.stringify({
                state: "canceled",
                rebalanceIds: [rebalance.rebalanceId],
              }),
            },
            processEmptyResponse,
          );

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const reject = useMutation({
            mutationFn: async () => {
              await rejectRebalance();

              const remainingRebalances = (rebalances ?? [])?.filter(
                (r) => r.id !== rebalance.rebalanceId,
              );

              queryClient.setQueryData([queryKey], {
                data: remainingRebalances,
              });
            },
            onError: notifyError("Reject failed"),
          });

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const cancel = useMutation({
            mutationFn: async () => {
              await cancelRebalance();

              const remainingRebalances = (rebalances ?? [])?.filter(
                (r) => r.id !== rebalance.rebalanceId,
              );

              queryClient.setQueryData([queryKey], {
                data: remainingRebalances,
              });
            },
            onError: notifyError("Cancel failed"),
          });

          // The group row of a swap is no proposal, and the API refuses to
          // reject or cancel a proposal a swap owns with 409 (swap R63,
          // R111): those change only from the swap (APP-1486).
          if (rebalance.level === "swap" || rebalance.positionSwapId != null) {
            return null;
          }

          return (
            <>
              <ActionButton
                variant="icon"
                icon="/icons/reject.svg"
                label="Reject"
                onClick={() => reject.mutate()}
                disabled={reject.isPending}
                type="button"
                className="me-2"
              />
              <ActionButton
                variant="icon"
                icon="/icons/trash.svg"
                label="Cancel"
                onClick={() => cancel.mutate()}
                disabled={cancel.isPending}
                type="button"
              />
            </>
          );
        },
      }),
    ],
    [queryClient, rebalances, queryKey, slantConnected, notifyError],
  );

  const rejectRow = useCallback(
    (id: number) => {
      const remainingRebalances = (rebalances ?? [])?.filter(
        (rebalance) => rebalance.id !== id,
      );

      queryClient.setQueryData([queryKey], {
        data: remainingRebalances,
      });
    },
    [queryClient, rebalances, queryKey],
  );

  const approveRow = useCallback(
    (id: number) => {
      const remainingRebalances = (rebalances ?? [])?.filter(
        (rebalance) => rebalance.id !== id,
      );

      queryClient.setQueryData([queryKey], {
        data: remainingRebalances,
      });
    },
    [queryClient, rebalances, queryKey],
  );

  const [savedColumnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    tableSettings.filters,
  );
  // R44: a saved Service Tier filter applies while the user is connected to
  // Slant. `useSlantConnected` is false until GET /profile has loaded, so the
  // saved filter is kept and only left out of the table while the user is not
  // connected — the column it filters is then not in the table.
  const columnFilters = useMemo(
    () =>
      slantConnected
        ? savedColumnFilters
        : withoutServiceTierFilter(savedColumnFilters),
    [slantConnected, savedColumnFilters],
  );
  // APP-1753, R24, R25, R41: a proposal of a swap that fails a filter of the
  // tab is left out before the grouping, so a group shows only the proposals
  // that pass every filter, and only while one does. Keyed by the ids left
  // out: a new array of rows resets the table's expanded rows.
  const failingSwapProposals = useMemo(
    () =>
      swapProposalsThatFail(
        listRows,
        columns as ColumnDef<RebalanceRow, unknown>[],
        columnFilters,
      ),
    [listRows, columns, columnFilters],
  );
  // APP-1486, swap spec R82: the proposals of a swap show in one group under
  // the name of the swap. Each row carries that name (R122), so the swap list
  // is not read (R123). Grouped after the custom field filter of
  // `useRebalanceListRows`: a swap row carries no custom fields, so filtering
  // after grouping would drop it.
  const rebalanceRows = useMemo(() => {
    const failing = new Set(failingSwapProposals.split(","));
    return groupPositionSwapRows(
      failingSwapProposals === ""
        ? listRows
        : listRows.filter((row) => !failing.has(String(row.rebalanceId))),
    );
  }, [listRows, failingSwapProposals]);
  const [sorting, setSorting] = useState<SortingState>(tableSettings.sorting);
  const [pagination, setPagination] = useState<PaginationState>({
    pageSize: tableSettings.pageSize,
    pageIndex: 0,
  });
  const [expanded, setExpanded] = useState<ExpandedState>({});
  // The group of a swap is always open (APP-1486).
  const expandedWithSwaps = useMemo<ExpandedState>(
    () =>
      expanded === true
        ? true
        : {
            ...expanded,
            ...Object.fromEntries(
              rebalanceRows
                .filter((row) => row.level === "swap")
                .map((row) => [positionSwapRowId(row.id), true]),
            ),
          },
    [expanded, rebalanceRows],
  );

  const { table, rowSelection, setRowSelection } = useTable({
    columns,
    data: rebalanceRows,
    getRowId: (row) =>
      row.level === "swap" ? positionSwapRowId(row.id) : getRebalanceRowId(row),
    getSubRows: getRebalanceSubRows,
    state: { columnFilters, sorting, pagination, expanded: expandedWithSwaps },
    onExpandedChange: setExpanded,
    onColumnFiltersChange: onColumnFiltersChange(
      columnFilters,
      setColumnFilters,
      tableSettings,
      setTableSettings,
    ),
    onSortingChange: onSortingChange(
      sorting,
      setSorting,
      tableSettings,
      setTableSettings,
    ),
    onPaginationChange: onPaginationChange(
      pagination,
      setPagination,
      tableSettings,
      setTableSettings,
    ),
    autoResetPageIndex: true,
    meta: {
      ...metaDefault,
      approveRow,
      deleteRow: rejectRow,
    },
  });

  setRowSelectionRef.current = setRowSelection;

  return isPendingRebalances || isPendingAssignees ? (
    <Loading />
  ) : (
    <>
      {submitResult && (
        <Alert
          variant={submitResult.type === "success" ? "success" : "danger"}
          dismissible
          onClose={() => setSubmitResult(null)}
          className="mb-3"
        >
          {submitResult.message}
        </Alert>
      )}
      {truncationWarnings.length > 0 && (
        <Alert
          variant="warning"
          dismissible
          onClose={() => setTruncationWarnings([])}
          className="mb-3"
        >
          <Alert.Heading as="h6">
            Schwab applied lot-instruction limits
          </Alert.Heading>
          <ul className="mb-0">
            {truncationWarnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </Alert>
      )}
      {/* R49: a failed read shows its error in place of the table, and no
          execute buttons for rows not shown. The query stays, so a change of
          it reads again. */}
      {!isErrorRebalances && (
        <Row className="mb-3">
          <Col md={canSubmitToSchwab ? 9 : 6}>
            <Form onSubmit={mutation.mutate} className="d-inline-block me-2">
              <ActionButton
                label="Execute Trades"
                icon="/icons/trade.svg"
                variant="secondary"
                type="submit"
                disabled={mutation.isPending}
              />
            </Form>
            <Form
              onSubmit={blockTradingMutation.mutate}
              className="d-inline-block"
            >
              <ActionButton
                label="Execute Block Trading"
                icon="/icons/trade.svg"
                variant="primary"
                type="submit"
                disabled={blockTradingMutation.isPending}
              />
            </Form>
            {canSubmitToSchwab && (
              <Form
                onSubmit={submitBlockTradingMutation.mutate}
                className="d-inline-block ms-2"
              >
                <ActionButton
                  label="Submit Block Trading"
                  icon="/icons/trade.svg"
                  variant="primary"
                  type="submit"
                  disabled={submitBlockTradingMutation.isPending}
                />
              </Form>
            )}
          </Col>
        </Row>
      )}
      <RebalanceQueryControls
        query={rqlQuery}
        onQueryChange={handleRqlQueryChange}
      />
      {isErrorRebalances ? (
        <FormError message="Failed to load the trade proposals." />
      ) : (
        <Table table={table} filterBarItems={filterBarItems} />
      )}
    </>
  );
};

export default RebalancesApprovedTable;
